import { Grid, Tooltip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import React, { useEffect, useState, useCallback } from "react";
import Helmet from "react-helmet";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PageviewIcon from "@mui/icons-material/Pageview";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { PagedTable, pageStore } from "../../common";
import { Portlet, PortletBody, PortletHeader, PortletHeaderToolbar } from "../../partials/content/Portlet";
import {
    supplementaryPromptsLanding,
    resetPage,
    set,
} from "../../store/actions/pages";
import { RootState } from "../../store/reducers";
import { useSupplementaryPromptsFields } from "./index";
import * as SUPPLEMENTARY_PROMPTS_API from "../../store/api/supplementaryPrompts";
import { useMarkdown } from "../../../hooks/ReactMarkdown";

const { usePageStore, useGridFilters } = pageStore(
    "supplementary-prompts-landing",
    {}
);

export default function SupplementaryPromptsPage() {
    const [refresh, setRefresh] = useState(Promise.resolve());
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [viewPromptDialogOpen, setViewPromptDialogOpen] = useState(false);
    const [selectedPrompt, setSelectedPrompt] = useState<any>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [transformedPromptsData, setTransformedPromptsData] = useState([]);
    const [showRawFormat, setShowRawFormat] = useState(false);
    const [isCopied, setIsCopied] = useState(false);
    const history = useHistory();
    
    const filters: any = useSelector<RootState>(
        (state) => state?.pages?.["supplementary-prompts-landing"]
    );
    const dispatch = useDispatch();

    const promptsData = usePageStore((s) => s.list || []);
    const pending = usePageStore((s) => s.pending);
    const fields = useSupplementaryPromptsFields();
    const tableFilter = useGridFilters();

    function handleRefresh() {
        setRefresh(Promise.resolve());
    }


    // Transform data to add contract names and prompt preview
    useEffect(() => {
        const transformed = promptsData.map(prompt => {
            // Extract contract names from supplementalPromptContractXref as an array
            const contractNames = prompt.supplementalPromptContractXref
                ?.map((xref: any) => xref.contract?.name)
                .filter(Boolean) || [];
            
            // Create prompt preview (first 100 characters)
            const promptPreview = prompt.prompt 
                ? (prompt.prompt.length > 100 
                    ? prompt.prompt.substring(0, 100) + '...' 
                    : prompt.prompt)
                : 'N/A';
            
            return {
                ...prompt,
                contractNames,
                promptPreview
            };
        });
        
        setTransformedPromptsData(transformed);
    }, [promptsData]);


    useEffect(() => {
        const pageName = "supplementary-prompts-landing";
        dispatch(set(pageName, "waitForCriteriaFilter", false));
        return () => {
            dispatch(resetPage(pageName));
        };
    }, [dispatch]);

    const handleViewPromptClick = useCallback((row: any) => {
        setSelectedPrompt(row);
        setViewPromptDialogOpen(true);
    }, []);

    const handleDeleteClick = useCallback((row: any) => {
        setSelectedPrompt(row);
        setDeleteDialogOpen(true);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        if (!selectedPrompt) return;

        setIsDeleting(true);
        try {
            await SUPPLEMENTARY_PROMPTS_API.deleteSuppPromptDocument(selectedPrompt.id);
            
            toast.success("Supplementary prompt deleted successfully", {
                autoClose: 5000,
            });
            setDeleteDialogOpen(false);
            setSelectedPrompt(null);
            setRefresh(Promise.resolve());
        } catch (error: any) {
            console.error("Error deleting prompt:", error);
            toast.error(error?.message || "Failed to delete prompt", {
                autoClose: 5000,
            });
        } finally {
            setIsDeleting(false);
        }
    }, [selectedPrompt]);

    const handleDeleteCancel = useCallback(() => {
        setDeleteDialogOpen(false);
        setSelectedPrompt(null);
    }, []);

    const handleViewPromptClose = useCallback(() => {
        setViewPromptDialogOpen(false);
        setSelectedPrompt(null);
        setShowRawFormat(false); // Reset to formatted view
        setIsCopied(false); // Reset copy state
    }, []);

    const handleCopyPrompt = useCallback(() => {
        if (selectedPrompt?.prompt) {
            navigator.clipboard.writeText(selectedPrompt.prompt).then(() => {
                setIsCopied(true);
                // Reset the checkmark after 2 seconds
                setTimeout(() => {
                    setIsCopied(false);
                }, 2000);
            }).catch((err) => {
                console.error('Failed to copy:', err);
                toast.error('Failed to copy to clipboard', {
                    autoClose: 3000,
                });
            });
        }
    }, [selectedPrompt]);

    const handleEditClick = useCallback((row: any) => {
        history.push(`/cms/supplementary-prompts/edit/${row.id}`);
    }, [history]);

    const handleAddClick = useCallback(() => {
        history.push("/cms/supplementary-prompts/add");
    }, [history]);

    const actionComponent = useCallback(
        (row: any) => {
            return (
                <div style={{ display: "flex", gap: "8px" }}>
                    <Tooltip title="View Prompt">
                        <IconButton
                            size="small"
                            onClick={() => handleViewPromptClick(row)}
                            color="primary"
                        >
                            <PageviewIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                        <IconButton
                            size="small"
                            onClick={() => handleEditClick(row)}
                            color="primary"
                        >
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(row)}
                            color="secondary"
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </div>
            );
        },
        [handleViewPromptClick, handleEditClick, handleDeleteClick]
    );

    function handleLoad(sort: any, filter: any, pagination: any) {
        dispatch(supplementaryPromptsLanding(pagination, sort, filter));
    }

    return (
        <>
            <Helmet title="Supplementary Prompts" />
            <Portlet>
                <PortletHeader
                    title="Supplementary Prompts"
                    toolbar={
                        <PortletHeaderToolbar>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={handleAddClick}
                            >
                                Add Prompt
                            </Button>
                        </PortletHeaderToolbar>
                    }
                />
                <PortletBody>
                    <Grid container>
                        <Grid item xs={12}>
                            <PagedTable
                                name="supplementary-prompts-landing"
                                fields={fields}
                                data={transformedPromptsData}
                                loading={pending}
                                onLoad={handleLoad}
                                onRefresh={handleRefresh}
                                refresh={refresh}
                                renderActionColumn={actionComponent}
                                hasActions={true}
                                filter={tableFilter}
                            />
                        </Grid>
                    </Grid>
                </PortletBody>
            </Portlet>


            {/* View Prompt Dialog */}
            <Dialog
                open={viewPromptDialogOpen}
                onClose={handleViewPromptClose}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Prompt Details
                </DialogTitle>
                <DialogContent dividers>
                    <div style={{ 
                        padding: "12px", 
                        backgroundColor: "#f5f5f5",
                        borderRadius: "4px",
                        position: "relative"
                    }}>
                        {showRawFormat && (
                            <Tooltip title={isCopied ? "Copied!" : "Copy to clipboard"}>
                                <IconButton
                                    onClick={handleCopyPrompt}
                                    size="small"
                                    color="secondary"
                                    style={{
                                        position: "absolute",
                                        top: "8px",
                                        right: "8px",
                                        backgroundColor: "white",
                                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                                    }}
                                >
                                    {isCopied ? (
                                        <CheckIcon style={{ color: "green" }} />
                                    ) : (
                                        <ContentCopyIcon />
                                    )}
                                </IconButton>
                            </Tooltip>
                        )}
                        {showRawFormat ? (
                            <pre style={{ 
                                margin: 0,
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                fontFamily: "monospace",
                                paddingRight: "40px" // Space for copy button
                            }}>
                                {selectedPrompt?.prompt || "N/A"}
                            </pre>
                        ) : (
                            useMarkdown({rawMarkdown: selectedPrompt?.prompt || "N/A"})
                        )}
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setShowRawFormat(!showRawFormat)} 
                        color="secondary"
                        variant="outlined"
                    >
                        {showRawFormat ? "View Formatted" : "View Raw"}
                    </Button>
                    <Button onClick={handleViewPromptClose} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this supplementary prompt?
                    <br />
                    <br />
                    <strong>Contract(s):</strong> {Array.isArray(selectedPrompt?.contractNames) ? selectedPrompt?.contractNames.join(', ') : selectedPrompt?.contractNames || 'N/A'}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} color="primary" disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="primary"
                        variant="contained"
                        disabled={isDeleting}
                    >
                        {isDeleting ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
